/**
 * CrashCourseStep.tsx
 * -------------------
 * Main navigation for the crash course steps.
 * - Handles step progression, module navigation, and point requirements.
 * - Integrates with localStorage and ProgressContext.
 */


import React, { useEffect, useState } from 'react';
import { useProgress } from '../../context/ProgressContext';
import { useHistory } from 'react-router-dom';
import Button from '../common/Button';
import Box from '../common/Box';

const CrashCourseStep = () => {
  const { language } = useProgress();
  const lang = (['en', 'fi', 'sv'].includes(language) ? language : 'fi') as 'en' | 'fi' | 'sv';
  const history = useHistory();
  const [showConfirm, setShowConfirm] = useState(false);
  const [showNotEnoughPoints, setShowNotEnoughPoints] = useState(false);
  const [currentStep, setCurrentStep] = useState(() => {
    const savedStep = localStorage.getItem('crashCourseCurrentStep');
    return savedStep !== null ? Number(savedStep) : 0;
  });

  const stepLabel = currentStep + 1;

  const nextStep = () => {
    if (currentStep < t.steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else if (Number(localStorage.getItem('points')) < 40) {
      setShowNotEnoughPoints(true);
    } else {
      localStorage.removeItem('crashCourseCurrentStep');
      history.push('/final-test');
    }
  };

  const toLearningModule = () => {
    if (currentStep === 0) {
      history.push('/part-one');
    } else if (currentStep === 1) {
      history.push('/part-two');
    } else if (currentStep === 2) {
      history.push('/part-three');
    } else if (currentStep === 3) {
      history.push('/part-four');
    } else {
      history.push('/final-test');
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      history.push('/');
    }
  };

  const t = {
    crashCourseBoxTitle: {
      en: 'Crash Course',
      fi: 'Tiiviskurssi',
      sv: 'Snabbkurs',
    },
    crashCourseBoxDescription: {
      en: 'Essential course for tourists to stay safe, when visiting Finland’s cold climate.',
      fi: 'Välttämätön kurssi turisteille turvallisuuden takaamiseksi Suomen kylmässä ilmastossa.',
      sv: 'En viktig kurs för turister för att vara säkra när de besöker Finlands kalla klimat.',
    },

    prevLabel: {
      en: '← Previous',
      fi: '← Edellinen',
      sv: '← Föregående',
    },
    nextLabel: {
      en: 'Next →',
      fi: 'Seuraava →',
      sv: 'Nästa →',
    },
    backToGamifiedLabel: {
      en: 'Back to Gamified',
      fi: 'Takaisin Peli-polkuun',
      sv: 'Tillbaka till spelvägen',
    },
    toLearningSlidesLabel: {
      en: 'To Learning Slides',
      fi: 'Oppimis-slideihin',
      sv: 'Till Lärande Slides',
    },
    backToLanding: {
      en: 'Back to Landing',
      fi: 'Takaisin Etusivulle',
      sv: 'Tillbaka till Landningssidan',
    },
    toFinalTest: {
      en: 'To Final Test',
      fi: 'Loppukokeeseen',
      sv: 'Till Slutprov',
    },
    confirmFinalTest: {
      en: 'Start Test',
      fi: 'Aloita Loppukoe',
      sv: 'Starta Slutprov',
    },
    backLabel: {
      en: 'Back',
      fi: 'Takaisin',
      sv: 'Tillbaka',
    },
    notEnoughPointsTitle: {
      en: 'Not Enough Points',
      fi: 'Ei Riittävästi Pisteitä',
      sv: 'Inte Tillräckligt Med Poäng',
    },
    contextForNotEnoughPoints: {
      en: (
        <>
          You need at least 40 points to proceed to the final test.
          <br />
          Please complete all the learning modules to earn enough points.
        </>
      ),
      fi: (
        <>
          Tarvitset vähintään 40 pistettä jatkaaksesi loppukokeeseen.
          <br />
          Suorita kaikki oppimismoduulit ansaitaksesi tarpeeksi pisteitä.
        </>
      ),
      sv: (
        <>
          Du behöver minst 40 poäng för att fortsätta den slutliga tentamen.
          <br />
          Fyll i alla inlärningsmoduler för att tjäna tillräckligt med poäng.
        </>
      ),
    },
    contextForConfimingFinalTest: {
      en: (
        <>
          Make sure you have read all the slides before proceeding further.
          <br />
          🫵 You will have to complete the test to unlock Wi-Fi.
          <br />
          📃 The test consists of multiple-choice questions based on the content you have learned.
          <br />
          ⚠️ The error margin is 75% of the total questions, meaning you can get 1 out of 3
          questions wrong.
          <br />
          Are you sure you want to proceed to the final test?
        </>
      ),
      fi: (
        <>
          Varmista, että olet lukenut kaikki diat ennen jatkamista.
          <br />
          🫵 Sinun on suoritettava koe, jotta Wi-Fi avautuu.
          <br />
          📃 Koe koostuu monivalintakysymyksistä, jotka perustuvat oppimaasi sisältöön.
          <br />
          ⚠️ Virhemarginaali on 75% kokonaiskysymyksistä, eli voit vastata väärin 1/3 kysymyksistä.
          <br />
          Oletko varma, että haluat siirtyä loppukokeeseen?
        </>
      ),
      sv: (
        <>
          Se till att du har läst alla slides innan du fortsätter.
          <br />
          🫵 Du måste genomföra testet för att låsa upp Wi-Fi.
          <br />
          📃 Testet består av flervalsfrågor baserade på det innehåll du har lärt dig.
          <br />
          ⚠️ Felmarginalen är 75% av totala frågorna, vilket innebär att du kan svara fel på 1 av 3
          frågor.
          <br />
          Är du säker på att du vill gå vidare till slutprovet?
        </>
      ),
    },
    confirmTitle: {
      en: 'Confirm',
      fi: 'Vahvistus',
      sv: 'Bekräfta',
    },
    steps: [
      {
        title: {
          en: 'Welcome',
          fi: 'Tervetuloa',
          sv: 'Välkommen',
        },
        content: {
          en: 'A quick guide to using the sauna and generally staying safe in Finland’s cold climate.',
          fi: 'Nopea opas saunan käyttämiseen ja yleinen opas pysyä turvallisena Suomen kylmässä ilmastossa.',
          sv: 'En snabb guide för att använda en bastu och en allmän guide för att hålla sig säker i Finlands kalla klimat.',
        },
      },
      {
        title: {
          en: 'Sauna Use',
          fi: 'Saunan käyttö',
          sv: 'Bastuanvändning',
        },
        content: {
          en: 'Step-by-step sauna safety from heating to cooling.',
          fi: 'Vaiheittainen opas saunan turvalliseen käyttöön.',
          sv: 'Steg-för-steg för säker bastuanvändning.',
        },
      },
      {
        title: {
          en: 'Gear & Stay Tips',
          fi: 'Varusteet & Majoitus',
          sv: 'Utrustning & Boende',
        },
        content: {
          en: 'How to use heaters, ventilate, and follow house rules in cold weather.',
          fi: 'Lämmittimien käyttö, ilmanvaihto ja talon säännöt kylmässä säässä.',
          sv: 'Använd värmare, ventilation och husregler i kallt väder.',
        },
      },
      {
        title: {
          en: 'Summary',
          fi: 'Yhteenveto',
          sv: 'Sammanfattning',
        },
        content: {
          en: 'Quick recap before your stay begins.',
          fi: 'Pikakertaus ennen vierailua.',
          sv: 'Snabb sammanfattning före vistelsen.',
        },
      },
    ],
  };

  const titleLabel = {
    en: t.steps[currentStep].title[lang],
    fi: t.steps[currentStep].title[lang],
    sv: t.steps[currentStep].title[lang],
  };

  const contentLabel = {
    en: t.steps[currentStep].content[lang],
    fi: t.steps[currentStep].content[lang],
    sv: t.steps[currentStep].content[lang],
  };
  useEffect(() => {
    localStorage.setItem('crashCourseCurrentStep', currentStep.toString());
  }, [currentStep]);

  useEffect(() => {
    const savedStep = localStorage.getItem('crashCourseCurrentStep');
    if (savedStep !== null) {
      setCurrentStep(Number(savedStep));
    }
  }, []);

  return (
    <div className="flex flex-col justify-center w-full min-h-full m-5">
      <div className="flex flex-col min-w-full min-h-full items-center justify-evenly">
        <Box className="hidden 2xl:flex flex-col items-center">
          <span className="text-6xl mb-4 drop-shadow-xl">📚</span>
          <h1
            className="text-4xl md:text-5xl font-extrabold 
            text-blue-900 text-center mb-2 leading-tight"
          >
            {t.crashCourseBoxTitle[lang]}
          </h1>
          <p
            className="text-lg text-blue-800 text-center 
          mb-8 max-w-xs font-medium"
          >
            {t.crashCourseBoxDescription[lang]}
          </p>
        </Box>
        <Box className="min-h-full max-w-full justify-evenly gap-6">
          <div className="flex flex-col items-center">
            <h2
              className="text-4xl font-extrabold text-blue-800
            text-center drop-shadow-sm mb-16"
            >
              {titleLabel[lang]}
            </h2>
            {/* Animated progress ring */}
            <div className="relative w-20 h-20">
              <svg className="absolute top-0 left-0" width="80" height="80">
                <circle cx="40" cy="40" r="34" fill="none" stroke="#e0e7ff" strokeWidth="8" />
                <circle
                  cx="40"
                  cy="40"
                  r="34"
                  fill="none"
                  stroke="#2563eb"
                  strokeWidth="8"
                  strokeDasharray={2 * Math.PI * 34}
                  strokeDashoffset={2 * Math.PI * 34 * (1 - stepLabel / 4)}
                  strokeLinecap="round"
                  style={{ transition: 'stroke-dashoffset 0.6s cubic-bezier(.4,2,.6,1)' }}
                  transform="rotate(90 40 40)"
                />
              </svg>
              <span
                className="absolute inset-0 flex items-center 
              justify-center text-blue-900 font-bold text-xl 
              font-mono bg-white/60 rounded-full shadow-inner"
              >
                {stepLabel}/4
              </span>
            </div>
          </div>
          <p
            className="text-xl text-blue-900 font-medium text-center 
          leading-relaxed font-sans"
            style={{ fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif' }}
          >
            {contentLabel[lang]}
          </p>
          <div className="flex flex-col w-full h-3/6 items-center justify-around gap-4">
            <Button
              gradient={false}
              className="bg-gradient-to-r from-green-600 to-green-500 
            hover:from-green-700 hover:to-green-600"
              onClick={toLearningModule}
            >
              {t.toLearningSlidesLabel[lang]}
            </Button>
            <div className=" flex flex-row justify-center gap-8 w-full">
              <Button
                gradient={false}
                className={
                  currentStep < 1
                    ? 'bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-700 hover:to-indigo-600'
                    : 'bg-gradient-to-r from-gray-800 to-gray-600 hover:from-gray-900 hover:to-gray-700'
                }
                onClick={prevStep}
              >
                {currentStep < 1 ? t.backToLanding[lang] : t.prevLabel[lang]}
              </Button>
              <Button
                gradient={currentStep <= 2}
                className={
                  currentStep > 2 ? 'bg-red-800 to-red-600 hover:from-red-900 hover:to-red-700' : ''
                }
                onClick={() => nextStep()}
              >
                {currentStep > 2 ? t.toFinalTest[lang] : t.nextLabel[lang]}
              </Button>
            </div>
            {showConfirm && (
              <div
                className="fixed inset-0 flex items-center justify-center
            backdrop-filter backdrop-blur-sm bg-black bg-opacity-50 z-50 "
              >
                <div className="bg-white rounded-lg p-8 shadow-lg">
                  <h2 className="text-lg font-bold mb-4">{t.confirmTitle[lang]}</h2>
                  <p className="mb-6">{t.contextForConfimingFinalTest[lang]}</p>
                  <div className="flex justify-center gap-8 mt-16">
                    <Button
                      gradient={false}
                      className="bg-gradient-to-r from-gray-800 to-gray-600 hover:from-gray-900 hover:to-gray-700"
                      onClick={() => setShowConfirm(false)}
                    >
                      {t.backLabel[lang]}
                    </Button>
                    <Button
                      gradient={false}
                      className="bg-red-800 to-red-600 hover:from-red-900 hover:to-red-700"
                      onClick={() => {
                        setShowConfirm(false);
                        history.push('/final-test');
                      }}
                    >
                      {t.confirmFinalTest[lang]}
                    </Button>
                  </div>
                </div>
              </div>
            )}
            {showNotEnoughPoints && (
              <div
                className="fixed inset-0 flex items-center justify-center
          backdrop-filter backdrop-blur-sm bg-black bg-opacity-50 z-50 "
              >
                <div className="bg-white rounded-lg p-8 shadow-lg">
                  <h2 className="text-lg font-bold mb-4">{t.notEnoughPointsTitle[lang]}</h2>
                  <p className="mb-6">{t.contextForNotEnoughPoints[lang]}</p>
                  <div className="flex justify-center gap-8 mt-16">
                    <Button
                      gradient={false}
                      className="bg-gradient-to-r from-gray-800 to-gray-600 hover:from-gray-900 hover:to-gray-700"
                      onClick={() => setShowNotEnoughPoints(false)}
                    >
                      {t.backLabel[lang]}
                    </Button>
                  </div>
                </div>
              </div>
            )}
            <Button
              gradient={false}
              className="bg-gradient-to-r from-purple-800 to-purple-600 
            hover:from-purple-900 hover:to-purple-700"
              onClick={() => history.push('/gamified-path')}
            >
              {t.backToGamifiedLabel[lang]}
              <span className="ml-2">🎮</span>
            </Button>
          </div>
        </Box>
      </div>
    </div>
  );
};
export default CrashCourseStep;
